import { Config } from "@config";
import type { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { http } from "bm-ui-kit";
import type { CompanyLegalForm } from "../LeadForm/types";
import type { FormFields } from "./SmeLoanHowMuchCreditSmallCalculator";
import { COMPANY_LEGAL_FORMS } from "./Steps/Step1.data";

export function calculateSmeLoan(values: FormFields): Promise<number> {
	const data = {
		actualFinancialData: {
			netIncome: values.netIncome,
			operatingProfit: values.operatingProfit,
			depreciation: values.depreciation,
			equity: values.equity,
		},
		loanGoal: values.loanGoal,
		companyLegalForm: values.companyLegalForm,
		rentalYearly: values.rentalYearly,
	};

	return http
		.post(Config.getApiUrl("/kkv-calculators/loan/get-available-loan-amount"), data)
		.then((response: Record<string, any>) => response.maxAvailableLoanAmount);
}

export function getCompanyLegalFormGroup(legalForm: CompanyLegalForm): string {
	// A) Bt., Kft., Zrt., Nyrt., Kkt, Szövetkezetek, Egyéni Cég, Egyéb vállalkozás
	if (["BT", "KFT", "ZRT", "NYRT", "KKT", "COOPERATIVE", "KFC", "OTHER"].includes(legalForm)) {
		return "a";
	}

	// B) Egyéni vállalkozás, őstermelő, családi gazdálkodó, Egyéni vállalkozás (KATA), Bt. (KATA)
	return "b";
}

interface FieldValues extends Record<string, any> {
	companyFoundYear: number;
	companyLegalForm: CompanyLegalForm;
	hasLessThanOneClosedYear: boolean;
	negativeLastClosedBusinessYear: boolean;
	negativeEquity: boolean;
	hasOverdueNavDebt: boolean;
	hasOverdueLoanDebt: boolean;
	hasAnotherCompanyUnderLiquidation: boolean;
	hasConnectedCompanyUnderLiquidation: boolean;
	ownerInKhr: boolean;
	ownerDenied: boolean;
	hadMoreThanHalfOwnerChange: boolean;
	hasNegativeIncomeAfterTaxation: boolean;
}

export function getKoList(values: FieldValues): string[] {
	const localKoList = [];
	const companyFoundYear = Number(values.companyFoundYear);
	const legalFormGroup = getCompanyLegalFormGroup(values.companyLegalForm);

	if (companyFoundYear >= new Date().getFullYear() - 1 || values.hasLessThanOneClosedYear) {
		localKoList.push("Sajnáljuk, de céged egy lezárt üzleti év hiányában nem alkalmas hitelfelvételre.");
	}

	if (values.negativeLastClosedBusinessYear) {
		localKoList.push(
			"Sajnáljuk, de céged év végi negatív üzemi/adózott eredménye miatt idén nem alkalmas hitelfelvételre.",
		);
	}

	if (values.negativeEquity) {
		localKoList.push("Sajnáljuk, de céged negatív saját tőkéje miatt idén nem alakalmas hitelfelvételre.");
	}

	if (values.hasOverdueNavDebt) {
		localKoList.push("Sajnáljuk, de céged élő NAV tartozás mellett nem alkalmas hitelfelvételre, rendezned kell.");
	}

	if (values.hasOverdueLoanDebt) {
		localKoList.push(
			"Sajnáljuk, de céged fennálló lejárt hiteltartozása miatt nem alkalmas újabb hitel felvételére.",
		);
	}

	if (values.hasAnotherCompanyUnderLiquidation) {
		localKoList.push(
			"Sajnáljuk, de céged tulajdonosi köréhez tartozó felszámolás alatt lévő társaság miatt nem alkalmas hitelfelvételre.",
		);
	}

	if (values.hasConnectedCompanyUnderLiquidation) {
		localKoList.push(
			"Sajnáljuk, de cégedhez kapcsolódó felszámolás alatt lévő társaság miatt nem alkalmas hitelfelvételre.",
		);
	}

	if (values.ownerInKhr) {
		if (legalFormGroup === "a") {
			localKoList.push(
				"Sajnáljuk, de céged tulajdonosi/vezetői körében lévő KHR/BAR listás státusz miatt nem alkalmas hitelfelvételre.",
			);
		}

		if (legalFormGroup === "b") {
			localKoList.push("Sajnáljuk, de a KHR/BAR listás státusz miatt nem alkalmas hitelfelvételre.");
		}
	}

	if (values.ownerDenied) {
		localKoList.push(
			"Sajnáljuk, de céged tulajdonosi/vezetői körében lévő vezetőségtől eltitltott státusz miatt nem alkalmas hitelfelvételre.",
		);
	}

	if (values.hadMoreThanHalfOwnerChange) {
		localKoList.push(
			"Sajnáljuk, de a fél éven belül történt 50%-nál nagyobb arányú tulajdonsováltás esetében céged induló vállalkozásnak minősül, így nincs elegendő gazdasági múltja ahhoz, hogy hitelképes legyen.",
		);
	}

	if (values.hasNegativeIncomeAfterTaxation) {
		localKoList.push("Sajnáljuk, de céged évi végi negatív eredménye miatt nem alkalmas hitelfelvételre.");
	}

	return localKoList;
}

export function getLeadOtherData(values: FieldValues, otherData: JsonDataBuilder): JsonDataBuilder {
	const {
		companyLegalForm,
		companyMainActivity,
		companyFoundYear,
		loanAmount,
		netIncome,
		ownerInKhr,
		negativeEquity,
		hasOverdueLoanDebt,
		hasOverdueNavDebt,
		hasAnotherCompanyUnderLiquidation,
		negativeLastClosedBusinessYear,
		hasLessThanOneClosedYear,
		hasNegativeIncomeAfterTaxation,
		hadNavCollection,
		hasRescheduledPublicDebt,
	} = values;

	otherData.addItem("Alapítás éve", "companyFoundYear", companyFoundYear);
	otherData.addItem(
		"Társasági forma",
		"companyLegalForm",
		COMPANY_LEGAL_FORMS.find((lf) => lf.value === companyLegalForm).label,
	);
	otherData.addItem("Fő tevékenység", "companyMainActivity", companyMainActivity);
	otherData.addItem("Igényelt hitelösszeg", "loanAmount", loanAmount, "ft");
	otherData.addItem("Utolsó lezárt év árbevétele", "netIncome", netIncome, "ft");
	otherData.addItem(
		getCompanyLegalFormGroup(companyLegalForm) === "a"
			? "Tulajdonosok/ügyvezetők/magánszemély készfizető kezesek szerepelnek a KHR/BAR listán?"
			: "Mint magánszemély, illetve a készfizető kezes szerepel a KHR/BAR listán?",
		"ownerInKhr",
		ownerInKhr,
	);
	otherData.addItem(
		"Van a tulajdonosi körben felszámolás alatt álló cég?",
		"hasAnotherCompanyUnderLiquidation",
		hasAnotherCompanyUnderLiquidation,
	);
	otherData.addItem("Utolsó lezárt pénzügyi év saját tőkéje negatív?", "negativeEquity", negativeEquity);
	otherData.addItem(
		"Utolsó lezárt pénzügyi év üzemi/adózott eredménye negatív?",
		"negativeLastClosedBusinessYear",
		negativeLastClosedBusinessYear,
	);
	otherData.addItem("Van a cégednek lejárt hiteltartozása?", "hasOverdueLoanDebt", hasOverdueLoanDebt);
	otherData.addItem("Van lejárt NAV tartozása a cégednek?", "hasOverdueNavDebt", hasOverdueNavDebt);
	otherData.addItem(
		"Céged kevesebb gazdasági múlttal rendelkezik, mint 1 teljes lezárt pénzügyi év?",
		"hasLessThanOneClosedYear",
		hasLessThanOneClosedYear,
		"bool",
	);
	otherData.addItem(
		"A vállalkozás adózás utáni jövedelme negatív?",
		"hasNegativeIncomeAfterTaxation",
		hasNegativeIncomeAfterTaxation,
		"bool",
	);
	otherData.addItem(
		"Volt inkasszó a vállalkozás számláján az elmúlt 36 hónapban?",
		"hadNavCollection",
		hadNavCollection,
		"bool",
	);
	otherData.addItem(
		"Van a cégednek átütemezett köztartozása? (pl. NAV részletfizetés)",
		"hasRescheduledPublicDebt",
		hasRescheduledPublicDebt,
		"bool",
	);

	return otherData;
}
